import { useSearchParams } from 'react-router-dom';
import { URL_PARAMS } from '../constants/urlParams';
import { tabs, TabId } from '../components/TabBar';

interface UrlParameters {
  userParam: string | null;
  cartaParam: string | null;
  tokenParam: string | null;
  staticDataParam: string | null;
  tabParam: string | null;
  menuParam: string | null;
  gameNameParam: string | null;
  launchCodeParam: string | null;
  partnerIdParam: string | null;
  platformIdParam: string | null;
  webViewParam: string | null;
  tipoFrontendParam: string | null;
  isStaticData: boolean;
  selectedTab: TabId;
  hasLaunchParams: boolean;
  hasUserParams: boolean;
  clearGameParams: () => void;
  clearWebViewParam: () => void;
  clearAllParams: () => void;
}

export function useUrlParameters(): UrlParameters {
  const [searchParams, setSearchParams] = useSearchParams();

  // Extract all parameters
  const userParam = searchParams.get(URL_PARAMS.USER);
  const cartaParam = searchParams.get(URL_PARAMS.CARTA);
  const tokenParam = searchParams.get(URL_PARAMS.TOKEN);
  const staticDataParam = searchParams.get(URL_PARAMS.STATIC_DATA);
  const tabParam = searchParams.get(URL_PARAMS.TAB);
  const menuParam = searchParams.get(URL_PARAMS.MENU);
  const gameNameParam = searchParams.get(URL_PARAMS.GAME_NAME);
  const launchCodeParam = searchParams.get(URL_PARAMS.LAUNCH_CODE);
  const partnerIdParam = searchParams.get(URL_PARAMS.PARTNER_ID);
  const platformIdParam = searchParams.get(URL_PARAMS.PLATFORM_ID);
  const webViewParam = searchParams.get(URL_PARAMS.WEB_VIEW);
  const tipoFrontendParam = searchParams.get(URL_PARAMS.TIPO_FRONTEND);

  // Derived values
  const isStaticData = staticDataParam === 'true';
  const isValidTab = tabParam && tabs.some(tab => tab.id === tabParam);
  const selectedTab = isValidTab ? tabParam as TabId : tabs[0].id;
  const hasLaunchParams = !!(launchCodeParam && platformIdParam);
  const hasUserParams = !!(userParam && cartaParam && tokenParam);

  const clearGameParams = () => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete(URL_PARAMS.GAME_NAME);
    newSearchParams.delete(URL_PARAMS.LAUNCH_CODE);
    newSearchParams.delete(URL_PARAMS.PARTNER_ID);
    newSearchParams.delete(URL_PARAMS.PLATFORM_ID);
    setSearchParams(newSearchParams);
  };

  const clearWebViewParam = () => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete(URL_PARAMS.WEB_VIEW);
    setSearchParams(newSearchParams);
  };

  const clearAllParams = () => {
    const newSearchParams = new URLSearchParams(searchParams);
    // Clear game launch parameters
    newSearchParams.delete(URL_PARAMS.GAME_NAME);
    newSearchParams.delete(URL_PARAMS.LAUNCH_CODE);
    newSearchParams.delete(URL_PARAMS.PARTNER_ID);
    newSearchParams.delete(URL_PARAMS.PLATFORM_ID);
    // Clear user parameters
    newSearchParams.delete(URL_PARAMS.USER);
    newSearchParams.delete(URL_PARAMS.CARTA);
    newSearchParams.delete(URL_PARAMS.TOKEN);
    // Clear webView parameter
    newSearchParams.delete(URL_PARAMS.WEB_VIEW);
    setSearchParams(newSearchParams);
  };

  return {
    userParam,
    cartaParam,
    tokenParam,
    staticDataParam,
    tabParam,
    menuParam,
    gameNameParam,
    launchCodeParam,
    partnerIdParam,
    platformIdParam,
    webViewParam,
    tipoFrontendParam,
    isStaticData,
    selectedTab,
    hasLaunchParams,
    hasUserParams,
    clearGameParams,
    clearWebViewParam,
    clearAllParams
  };
}